import { Button, Dropdown, Input, ModalWrapper } from '@/shared';
import type { DropdownOption } from '@/shared/components/ui/Dropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { useFileUpload } from '@/shared/hooks/useFileUpload';
import { useCreateResource } from '@/features/resources/hooks/useCreateResource';

// -----------------------------
// Dropdown options
// -----------------------------
const categories: DropdownOption<string>[] = [
  { label: 'Template', value: 'TEMPLATE' },
  { label: 'Guide', value: 'GUIDE' },
  { label: 'Policy Document', value: 'POLICY_DOCUMENT' },
  { label: 'External Link', value: 'EXTERNAL_LINK' },
];

// -----------------------------
// Component
// -----------------------------
interface AddResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddResourceModal: React.FC<AddResourceModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { upload, uploading, progress, error: uploadError } = useFileUpload();
  const { mutate: createResource, isPending: isCreating } = useCreateResource();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedResource, setUploadedResource] = useState<{
    path: string;
    url: string;
  } | null>(null);

  // ✅ Fixed Validation Schema
  const schema = yup.object({
    title: yup.string().required('Title is required'),
    details: yup.string().required('Details are required'),
    category: yup.string().required('Category is required'),
  });

  // ✅ Explicitly define the form data type
  interface ResourceFormData {
    title: string;
    details: string;
    category: string;
  }

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ResourceFormData>({
    resolver: yupResolver(schema) as Resolver<ResourceFormData>,
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setUploadedResource(null); // Reset upload status on new file selection
    }
  };

  const handleUploadClick = async () => {
    if (!selectedFile) return;

    const result = await upload(selectedFile, 'resources');
    console.log('result--> ', result);
    if (result) {
      setUploadedResource(result);
    }
  };

  const onSubmit = async (data: ResourceFormData) => {
    if (!uploadedResource) {
      // You might want to show an error here if file is mandatory
      alert('Please upload a document first');
      return;
    }

    const payload = {
      title: data.title,
      description: data.details,
      category: data.category,
      fileUrl: uploadedResource.path,
      // You can send fileType if backend supports it
      fileType: selectedFile?.type,
    };

    createResource(payload, {
      onSuccess: () => {
        onClose();
        // Reset local state if needed
        setSelectedFile(null);
        setUploadedResource(null);
      },
    });
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen p-5">
        <div className="bg-white rounded-4xl p-8 max-w-2xl w-full">
          <h3 className="text-3xl font-bold mb-2 text-text-primary">
            Add resources
          </h3>
          <p className="mb-8 text-xl text-text-secondary">
            Manage your properties with ease
          </p>

          {/* Form */}
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <Input
              label="Title"
              placeholder="Add title"
              {...register('title')}
              error={errors.title?.message}
              className="!text-xl"
            />

            <div className="flex flex-col gap-1">
              <label className="font-medium text-text-secondary">Details</label>
              <textarea
                {...register('details')}
                placeholder="Details about the resource"
                className="w-full rounded-md border border-gray-300 p-3 text-xl h-[100px] focus:outline-none resize-none"
              />
              {errors.details?.message && (
                <p className="text-red-500 text-sm">{errors.details.message}</p>
              )}
            </div>

            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Dropdown
                  label="Category"
                  buttonClassName="!w-full"
                  options={categories}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.category && (
              <p className="text-red-500 text-sm">{errors.category.message}</p>
            )}

            {/* File Upload Section */}
            <div className="space-y-3">
              <label className="font-medium text-text-secondary">
                Document Upload
              </label>

              {!selectedFile ? (
                <div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col gap-4 items-center justify-center">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 13.5V3"
                            stroke="#141414"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M20.25 13.5V19.5H3.75V13.5"
                            stroke="#141414"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8.25 6.75L12 3L15.75 6.75"
                            stroke="#141414"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p className="text-sm">Click to choose a file (PDF)</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Size limit: 10MB
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              ) : (
                <div className="border border-gray-100 rounded-xl p-4 bg-[#F8F9FA]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="p-2 bg-white rounded-lg border border-gray-100">
                        <svg
                          className="w-5 h-5 text-[#5761EB]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <div className="truncate font-medium text-sm text-text-primary">
                        {selectedFile.name}
                      </div>
                    </div>

                    {/* Clear selection button */}
                    {!uploading && !uploadedResource && (
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="text-gray-400 hover:text-[#5761EB] transition-colors p-1"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Upload Info/Progress */}
                  {uploading && (
                    <div className="space-y-1">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-[#5761EB] h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-right text-text-secondary font-medium">
                        {Math.round(progress)}%
                      </p>
                    </div>
                  )}

                  {uploadedResource && (
                    <div className="flex items-center gap-2 text-[#24C661] text-sm font-medium bg-white p-2 rounded-lg border border-[#24C661]/20">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Upload Complete</span>
                    </div>
                  )}

                  {uploadError && (
                    <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-2 rounded-lg border border-red-100">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span className="truncate">{uploadError}</span>
                    </div>
                  )}

                  {/* Separate Upload Button */}
                  {!uploadedResource && !uploading && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full mt-3 border-[#5761EB]! text-[#5761EB]! hover:bg-[#5761EB]! hover:text-white! transition-all"
                      onClick={handleUploadClick}
                    >
                      Upload Document
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="pt-6">
              <Button
                variant="primary"
                size="lg"
                type="submit"
                className="w-full mb-4 !font-bold !text-xl"
                disabled={!uploadedResource || uploading || isCreating}
                isLoading={isCreating}
              >
                Publish now
              </Button>
            </div>
          </form>
        </div>
      </div>
    </ModalWrapper>
  );
};
